import React, { useState, useEffect, useGlobal } from "reactn";
import axios from "axios";
import _ from "lodash";
import { format, parse } from "date-fns";

import { useDateFilter, useListings } from "../../../providers/reports";

import {
  DateFilter,
  ReportContainer,
  ReportTable
} from "../../../components/UI/reportsAnalytics";
import { useCancellableRequest } from "../../../utilities/hooks/useCancellableRequest";

const csvHeaders = [
  { label: "Full Name", key: "name" },
  { label: "Date", key: "date" },
  { label: "Attendance Status", key: "status" },
  { label: "Program", key: "program" }
];

const ReportsAttendance = () => {
  const global = useGlobal()[0];
  const [partnerPathway, setPartnerPathway] = useGlobal("partnerPathway");
  const [loading, setLoading] = useGlobal("loading");
  const [dateFrom, dateTo, setDateFrom, setDateTo] = useDateFilter();
  const { programs, onlineVirtuals } = useListings();
  const { makeRequest } = useCancellableRequest();
  const [selectedProgram, setSelectedProgram] = useState("");
  const [listingType, setListingType] = useState("Program");
  const [studentsList, setStudentsList] = useState([]);
  const [submittedFilters, setSubmittedFilters] = useState(null);
  const appliedDateFrom = submittedFilters?.from || null;
  const appliedDateTo = submittedFilters?.to || null;
  const appliedProgram = submittedFilters?.program || "";
  const appliedListingType = submittedFilters?.listingType || "Program";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const results = await makeRequest({
          method: "get",
          url: `${process.env.REACT_APP_API}/partner/attendance/reports/${global.organizationInfo.partnerId}`
        });

        if (!results) return;

        if (results.data.success) {
          setStudentsList(results.data.data.students);
        }
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setPartnerPathway([
      ...partnerPathway.slice(0, 1),
      { label: "Reports & Analytics", to: "/reporting" },
      { label: "Attendance", to: "/reports/attendance" }
    ]);
  }, []);

  const displayAdditional = r => {
    if (r.status === "Reduced") {
      return `(${r.percent}%)`;
    }
    if (r.status === "Extended") {
      return `($${r.fee})`;
    }
    if (r.status === "Absent" && r.percent !== 0) {
      return `(${r.percent}%)`;
    }
    return "";
  };

  /**
   * Filters students by program selected and by date range selected.
   *
   * @returns {Array} Filtered array of students, sorted by date.
   */
  const getFilteredStudents = () => {
    if (!submittedFilters) return [];

    let filteredStudents = studentsList;

    if (appliedProgram !== "") {
      const dateFromUnix = new Date(appliedDateFrom).getTime();
      const tempDateTo = new Date(appliedDateTo);
      const dateToUnix = tempDateTo.setDate(tempDateTo.getDate() + 1);

      filteredStudents = studentsList
        .filter(e => {
          const programDate = new Date(e.attDate).getTime();
          return (
            e.program_name === appliedProgram &&
            programDate >= dateFromUnix &&
            programDate <= dateToUnix
          );
        })
        .sort((a, b) => new Date(a.attDate) - new Date(b.attDate));
    }

    return filteredStudents;
  };

  const displayProgram = () => {
    const filteredStudents = getFilteredStudents();

    if (filteredStudents.length === 0 || selectedProgram === "") {
      return [];
    }

    return filteredStudents.map(r => {
      return [
        r.fullName || r.displayName,
        format(new Date(r.attDate), "MMM d, yyyy"),
        `${r.status} ${displayAdditional(r)}`
      ];
    });
  };

  /**
   * Gets the CSV data for the CSVLink component to use.
   *
   * Currently, no checking is done to ensure the resulting data is non-empty.
   *
   * @returns {Array} Array of objects {name, date, status}.
   */
  const getCsvData = () => {
    const filteredStudents = getFilteredStudents();
    return filteredStudents.map(student => {
      return {
        name: student.fullName || student.displayName,
        date: format(new Date(student.attDate), "MMM d, yyyy"),
        status: student.status + displayAdditional(student),
        program: selectedProgram
      };
    });
  };

  let listings = [];
  switch (submittedFilters ? appliedListingType : listingType) {
    case "Program":
      listings = programs;
      break;

    case "Online":
      listings = onlineVirtuals;
      break;

    default:
      break;
  }

  let reportSubtitles = [
    "Attendance",
    submittedFilters
      ? `For ${format(
          parse(appliedDateFrom, "yyyy-MM-dd", new Date()),
          "MMM d, yyyy"
        )}
         - 
         ${format(
           parse(appliedDateTo, "yyyy-MM-dd", new Date()),
           "MMM d, yyyy"
         )}`
      : "Select filters and click Apply"
  ];

  if (submittedFilters && appliedProgram) {
    reportSubtitles.push(appliedProgram);
  }

  return (
    <div className="admin">
      <div className="container-fluid adminprofiles">
        <div className="row cont">
          <div className="col-md-6">
            <h1>Attendance</h1>
          </div>
        </div>

        <div className="filter-container">
          <div className="filter-section">
            <DateFilter
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />
            <div className="filter-item grid-new-col">
              <label htmlFor="client_name" className="filter-label">
                Listings Type
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: window.innerWidth < 768 ? "8px" : "10px",
                  alignItems: window.innerWidth < 768 ? "stretch" : "center"
                }}
              >
                <select
                  name="client_name"
                  id="client_name"
                  type="text"
                  className="form-control filter-input"
                  value={listingType}
                  onChange={e => {
                    setListingType(e.target.value);
                    setSelectedProgram("");
                  }}
                  style={{
                    flex: window.innerWidth < 768 ? "1" : "0 0 auto"
                  }}
                >
                  <option value="Program">Program</option>
                  <option value="Online">Online Virtual</option>
                </select>
                <select
                  name="program"
                  id="program"
                  type="text"
                  className="form-control filter-input"
                  value={selectedProgram}
                  onChange={e => setSelectedProgram(e.target.value)}
                  style={{
                    flex: window.innerWidth < 768 ? "1" : "0 0 auto"
                  }}
                >
                  <option value="" disabled selected hidden>
                    Please Select
                  </option>
                  {listings.map((e, i) => {
                    return (
                      <option value={e.program_name} key={i}>
                        {e.program_name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <button
                className="btn btn-primary ml-3"
                onClick={() => {
                  setSubmittedFilters({
                    from: dateFrom,
                    to: dateTo,
                    program: selectedProgram,
                    listingType
                  });
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
        <ReportContainer
          title="Attendance"
          subtitles={reportSubtitles}
          csvHeaders={csvHeaders}
          csvData={submittedFilters ? getCsvData() : []}
          fileName={
            submittedFilters
              ? `Attendance_${appliedDateFrom}_${appliedDateTo}.csv`
              : "Attendance.csv"
          }
        >
          <div className="report-table-large">
            <ReportTable
              headings={[
                { label: "Student Name", className: "table-item-bold" },
                { label: "Date" },
                {
                  label: "Status",
                  className: "table-item-money",
                  style: { textAlign: "right" }
                }
              ]}
              body={submittedFilters ? displayProgram() : []}
              noDataText="No attendance data found for the selected filters!"
            />
            {!submittedFilters && (
              <p style={{ textAlign: "center", marginTop: 20 }}>
                Select filters and press Apply to view report.
              </p>
            )}
          </div>
        </ReportContainer>
      </div>
    </div>
  );
};

export default ReportsAttendance;
